import {AsyncPipe} from '@angular/common';
import {Component, ElementRef, HostListener, Input, OnInit, ViewChild} from '@angular/core';
import {MatButton} from '@angular/material/button';
import {MatDialog} from '@angular/material/dialog';
import {MatIcon} from '@angular/material/icon';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {MatTooltip} from '@angular/material/tooltip';
import {Asset, AssetService, AssetType, deserializeAsset} from '@app/asset';
import {CropperDialogComponent} from '@app/cropper/cropper-dialog/cropper-dialog.component';
import {ManagedComponent} from '@app/shared/managed.component';
import {User} from '@app/shared/user.service';

import {fromEvent, mergeMap, Observable} from 'rxjs';
import {filter, startWith} from 'rxjs/operators';

@Component({
    selector: 'frog-thumbnail-inspector',
    templateUrl: './thumbnail-inspector.component.html',
    styleUrls: ['./thumbnail-inspector.component.scss'],
    imports: [MatProgressSpinner, MatButton, MatIcon, MatTooltip, AsyncPipe],
})
export class ThumbnailInspectorComponent extends ManagedComponent implements OnInit {
    @ViewChild('image') image: ElementRef;
    @ViewChild('file') file: ElementRef;
    @Input() asset: Asset;
    @Input() user: User;

    public uploading: boolean;
    public uploadProgress$: Observable<number>;
    public AssetType = AssetType;

    constructor(
        private assetService: AssetService,
        private matDialog: MatDialog,
    ) {
        super();
    }

    ngOnInit(): void {
        this.asset = deserializeAsset(this.asset);
        const sub = this.assetService.assetUpdated
            .pipe(filter((asset) => asset.id === this.asset.id))
            .subscribe((asset) => {
                this.asset = asset;
            });
        this.subscriptions.push(sub);
    }

    @HostListener('window:paste', ['$event'])
    pasteImage(event: ClipboardEvent) {
        const matchType = /image.*/;
        const clipboardData = event.clipboardData;
        const files: File[] = Array.from(clipboardData.items)
            .filter((item) => item.type.match(matchType))
            .map((item) => item.getAsFile());

        if (files.length === 0) {
            return;
        }

        this.uploadFile(files[0]);
    }

    crop() {
        const dialog = this.matDialog.open(CropperDialogComponent, {
            width: '80vw',
            data: this.asset,
        });
        dialog.afterClosed().subscribe((cropRect) => {
            if (cropRect) {
                this.assetService.cropImage(this.asset, cropRect);
            }
        });
    }

    browseFiles() {
        this.file.nativeElement.click();
    }

    sendFile() {
        if (this.file.nativeElement.files.length === 0) {
            return;
        }

        this.uploadFile(this.file.nativeElement.files[0]);
    }

    uploadFile(file: File) {
        this.uploading = true;
        this.uploadProgress$ = this.assetService
            .updateAssetThumbnail(file, this.asset)
            .pipe(startWith(0));

        this.uploadProgress$
            .pipe(
                mergeMap(() => {
                    return fromEvent(this.image.nativeElement, 'load');
                }),
            )
            .subscribe(() => {
                this.uploading = false;
            });
    }

    resetThumbnail() {
        this.assetService.resetThumbnail(this.asset);
    }
}
